import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./PublishPackageSummary.css";
import { useLocation, useNavigate } from "react-router-dom";
import { publishPackage, getPackageInfo, deletePackage } from "../../../services/scm/Package";
import { formatToBrowserTime } from "../../../utils/timeFormat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faUnlock } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useFeatures } from "../../../utils/featureContext";
import { FEATURES } from "../../../utils/extractFeaturesList";
import LoadingOverlay from "../../../components/loadingoverlay/LoadingOverlay";

export default function PublishPackageSummary({ title }) {
  const [packageDetails, setPackageDetails] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();

  const { features, loading } = useFeatures();

  const hasAccess = (featureName) => {
    if (loading) return false;
    return features?.includes(featureName);
  };

  const isScmAdmin = hasAccess(FEATURES.SCM_ADMIN);

  const packageName = state?.packageName ?? "";
  const versionName = state?.versionName ?? "";

  const params = { packageName, versionName };

  // navigate helpers
  const goEditPackage = () => {
    navigate("/software-management/edit-package", {
      state: { packageName, versionName },
    });
  };

  const goEditDependencies = () =>
    navigate("/software-management/select-dependencies", {
      state: { packageName, versionName },
    });

  async function loadData() {
    setIsLoading(true);
    try {
      const response = await getPackageInfo(params);
      if (!Array.isArray(response) || response.length === 0) return;

      const pkg = response.find(
        item =>
          item.packageName === params.packageName &&
          item.version === params.versionName
      );
      setPackageDetails(pkg);
    } finally {
      setIsLoading(false);
    }
  }

  const handlePublish = async () => {
    const payload = { packageName, versionName };

    try {
      await publishPackage(payload);

      toast.success(
        `${packageName} : ${versionName} Published Successfully`
      );

      navigate("/software-management/find-package");

    } catch (err) {
      console.log("Publish Package Error Response", err?.response);
      const is409 = err?.response?.status === 409;
      const apiError = err?.response?.data;

      console.log("Publish Package Error Response", err?.response);

      if (is409 && apiError?.error) {
        const missingKeys = apiError?.missingKeys || [];

        const message = (
          <div>
            <div>Some file(s) are missing</div>

            {missingKeys.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <div>Missing Files:</div>
                <ul style={{ margin: 0, paddingLeft: "18px" }}>
                  {missingKeys.map((key, index) => (
                    <li key={index}><strong>{key}</strong></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

        toast.error(message, {
          autoClose: false,   // stays until user closes
          closeOnClick: true,
        });

      } else {
        toast.error(
          `Failed to Publish Package - ${packageName} : ${versionName}, Please Try Again`
        );
      }

      console.error("Publish Package Failed", err);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${packageName} : ${versionName}? This action cannot be undone.`
    );
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await deletePackage({ packageName, versionName });
      toast.success(`${packageName} : ${versionName} Deleted Successfully`);
      navigate("/software-management/find-package");
    } catch (err) {
      const is409 = err?.response?.status === 409;
      const apiError = err?.response?.data?.error;

      const errorMessage = is409 && apiError
        ? `Cannot delete ${packageName} : ${versionName}. ${apiError}`
        : `Failed to delete ${packageName} : ${versionName}. Please try again.`;

      toast.error(errorMessage);
      console.error("Delete Package Failed", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => navigate("/software-management/find-package");

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!packageName || !versionName)
      navigate("/software-management/find-package");
  }, [packageName, versionName, navigate]);

  const isDraft = packageDetails?.status !== "PUBLISHED";
  const isPageReady = !loading && !isLoading && packageDetails !== null;


  return (
    <div className="container my-3 ">
      {!isPageReady && <LoadingOverlay text={t("Loading Package Details...")} />}
      <div className="card-body col-12 col-xxl-10 responsive-col">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">{t(title || "Package Summary")}</h5>
        </div>

        {/* Package Info Section */}
        <div className="card mt-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="card-subtitle mb-0">{t("Package Info")}</h6>
              {isDraft && (
                <button
                  className="btn btn-transperant"
                  onClick={goEditPackage}
                >
                  {t("Edit")}
                </button>
              )}
            </div>
            <div className="row g-2">
              <div className="col-6 col-md-3 fw-semibold">
                {t("Package Name")}:
              </div>
              <div
                className="col-6 col-md-3 text-truncate"
                title={packageDetails?.packageName}
              >
                {packageDetails?.packageName}
              </div>
              <div className="col-6 col-md-3 fw-semibold">
                {t("Package Version")}:
              </div>
              <div className="col-6 col-md-3 d-flex align-items-center gap-1">
                {packageDetails?.version}
                {packageDetails?.status === "PUBLISHED" ? (
                  <FontAwesomeIcon
                    icon={faLock}
                    style={{ color: "green" }}
                    title="Published - Locked"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faUnlock}
                    style={{ color: "red" }}
                    title="Draft - Unlocked"
                  />
                )}
              </div>

              <div className="col-6 col-md-3 fw-semibold">
                {t("Package Description")}:
              </div>
              <div
                className="col-6 col-md-3 description-text"
                title={packageDetails?.description}
              >
                {packageDetails?.description || "--"}
              </div>
              <div className="col-6 col-md-3 fw-semibold">
                {t("Asset Type")}:
              </div>
              <div className="col-6 col-md-3">
                {packageDetails?.assetType || "--"}
              </div>
              <div className="col-6 col-md-3 fw-semibold">
                {t("Expiration")}:
              </div>
              <div className="col-6 col-md-3">{packageDetails?.expiration === "Never" ? "Never" : formatToBrowserTime(packageDetails?.expiration)}</div>
            </div>
          </div>
        </div>

        {/* Package Instructions */}
        <div className="card mt-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="card-subtitle mb-0">
                {t("Package Instructions")}
              </h6>
              {isDraft && (
                <button
                  className="btn btn-transperant"
                  onClick={goEditPackage}
                >
                  {t("Edit")}
                </button>
              )}
            </div>
            <div className="row g-2">

              {/* Job Type */}
              <div className="col-6 col-md-3 fw-semibold">
                {t("Job Type")}:
              </div>
              <div className="col-6 col-md-9">
                {packageDetails?.jobType || "No Data"}
              </div>

              {/* Clear Target */}
              <div className="col-6 col-md-3 fw-semibold">
                {t("Clear Target")}:
              </div>
              <div className="col-6 col-md-9">
                {packageDetails?.clearTarget ? "True" : "False"}
              </div>

              {/* Destination */}
              <div className="col-6 col-md-3 fw-semibold">
                {t("Destination")}:
              </div>
              <div className="col-6 col-md-9">
                {packageDetails?.destination || "No Data"}
              </div>

              {/* S3 Keys */}
              <div className="col-6 col-md-3 fw-semibold">
                {t("Files")}:
              </div>
              <div className="col-6 col-md-9">
                <div className="border rounded p-1 bg-light">
                  {packageDetails?.s3Keys?.length > 0 ? (
                    packageDetails?.s3Keys.map((key, index) => (
                      <div key={index} className="pt-1">
                        {key}
                      </div>
                    ))
                  ) : (
                    <div>No Data</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dependencies Section */}
        <div className="card mt-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="card-subtitle mb-0">{t("Dependencies")}</h6>
              {isDraft ? (
                <button
                  type="button"
                  className="btn btn-transperant"
                  onClick={goEditDependencies}
                >
                  {t("Edit")}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-transperant"
                  onClick={goEditDependencies}
                >
                  {t("View")}
                </button>
              )}
            </div>

            <div className="row g-2">
              <div className="col-6 col-md-9">
                <div className="border rounded p-2 bg-light">
                  {packageDetails?.dependencies?.length > 0 ? (
                    packageDetails.dependencies.map((dep, index) => {
                      const groupName =
                        dep.split("thinggroup/")[1]?.replace(/_/g, " ") || dep;
                      return (
                        <div key={index} className="pt-1 ">
                          {groupName}
                        </div>
                      );
                    })
                  ) : (
                    <div>No Data</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 d-flex justify-content-end gap-2">
          {isScmAdmin && (
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  {t("Delete")}
                </>
              ) : (
                t("Delete")
              )}
            </button>
          )}
          {isDraft && (
            <button className="btn btn-primary" onClick={handlePublish}>
              {t("Publish")}
            </button>
          )}
          <button className="btn btn-primary" onClick={handleCancel}>
            {t("Cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}